/* eslint-disable @typescript-eslint/naming-convention */
import { injectable } from '@servicetitan/react-ioc';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { CalculatorApi, TroubleshootingToolsRequestModel } from '../../common/api/calculator.api';
import axios from 'axios';
import { getBaseRequestParams } from '../../common/utils/base-request-params';
import { MainStore } from '../../common/stores/main.store';
import { CalculatorBaseRequestDto } from '../models/calculator.dto';
import { ChatStore } from '../../common/stores/chat.store';
import { MessageType } from '../../common/models/message-type.model';

export enum CalculatorType {
    AirflowCalculator = 'airflow-calculator',
    SuperHeat = 'super-heat',
    AirflowDiagnostic = 'airflow-diagnostic',
    SubCool = 'sub-cool',
    DuctSizingCalculator = 'duct-sizing-calculator',
    NitrogenPressureTest = 'nitrogen-pressure-test',
    PressureSetPoints = 'pressure-set-points',
    TXVSizeCalculator = 'txv-size-calculator',
    TargetSuperHeat = 'target-super-heat',
}

const calculators: { type: CalculatorType; displayName: string; disabled?: boolean }[] = [
    {
        type: CalculatorType.SuperHeat,
        displayName: 'SuperHeat',
    },
    {
        type: CalculatorType.TargetSuperHeat,
        displayName: 'Target Super Heat',
    },
    {
        type: CalculatorType.AirflowDiagnostic,
        displayName: 'Airflow Diagnostic',
    },
    {
        type: CalculatorType.SubCool,
        displayName: 'SubCool',
    },
    {
        type: CalculatorType.DuctSizingCalculator,
        displayName: 'Duct Sizing Calculator',
    },
    {
        type: CalculatorType.NitrogenPressureTest,
        displayName: 'Nitrogen Pressure Test',
    },
    {
        type: CalculatorType.PressureSetPoints,
        displayName: 'Pressure Set Points',
    },
    {
        type: CalculatorType.TXVSizeCalculator,
        displayName: 'TXV Size Calculator',
    },
];

@injectable()
export class CalculatorStore {
    @observable isLoading: boolean = false;

    @observable selectedCalculator?: CalculatorType = undefined;

    constructor(
        private readonly calculatorApi: CalculatorApi,
        private readonly mainStore: MainStore,
        private readonly chatStore: ChatStore
    ) {
        makeObservable(this);
    }

    @computed
    get calculators() {
        return calculators;
    }

    @computed
    get calculatorByType() {
        return new Map<CalculatorType, string>(
            calculators.map(calculator => [calculator.type, calculator.displayName])
        );
    }

    @computed
    get gasTypes() {
        return [
            'R-22',
            'R-134A',
            'R-404A',
            'R-407C',
            'R-410A',
            'R-421A',
            'R-422B',
            'R-422D',
            'R-427A',
            'R-438A',
            'R-458A',
            'R-507',
        ].map(x => {
            return { id: x, name: x };
        });
    }

    @action
    setSelectedCalculator = (selectedCalculator: CalculatorType) => {
        this.selectedCalculator = selectedCalculator;

        this.chatStore.enqueueMessages([
            {
                type: MessageType.Text,
                isMine: true,
                showBubble: true,
                date: new Date(),
                text: this.calculatorByType.get(selectedCalculator),
            },
            {
                type: MessageType.CalculatorForm,
                isMine: false,
                showAvatar: true,
                showBubble: true,
                date: new Date(),
                data: selectedCalculator,
            },
        ]);
    };

    @action
    dispose = () => {
        // Clean up resources if needed
    };

    @action
    submitCalculation = async (calcParams: CalculatorBaseRequestDto) => {
        this.isLoading = true;
        const params: TroubleshootingToolsRequestModel = {
            id: window.crypto.randomUUID(),
            type: 'tool',
            question: JSON.stringify(calcParams),
            job_id: this.mainStore.selectedAppointment!.id,
            ...getBaseRequestParams(),
        };
        try {
            const response = await this.calculatorApi.sendTroubleshootingCalculation(params);
            const result = JSON.parse(response.data.answer.answer_text.replace(/\\/g, '')).data;

            this.chatStore.enqueueMessages([
                {
                    type: MessageType.CalculationResult,
                    isMine: false,
                    showBubble: false,
                    date: new Date(),
                    data: {
                        type: this.selectedCalculator,
                        inputParams: calcParams.tool_input,
                        result,
                    },
                },
            ]);
            runInAction(() => {
                this.isLoading = false;
            });
        } catch (error) {
            if (axios.isCancel(error)) {
                // skip
                return;
            }
            runInAction(() => {
                this.isLoading = false;
            });
            if (axios.isAxiosError(error)) {
                return;
            }
            throw error;
        }
    };
}
